import React, { useState, useEffect } from 'react';
import { Droplet, TrendingUp, Users, Award, Share2 } from 'lucide-react';
import StatsCard from '../components/StatsCard';
import CounterDisplay from '../components/CounterDisplay';
import WaterAnimation from '../components/WaterAnimation';
import ShareImpactCard from '../components/ShareImpactCard';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PublicDisplay() {
  const [stats, setStats] = useState({
    totalBottlesSaved: 0,
    totalRefills: 0,
    totalCustomers: 0,
    thisMonth: 0
  });
  const [topCustomers, setTopCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/stats`);
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
        setTopCustomers(data.topCustomers || []);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // Auto-refresh every 30 seconds for live updates
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/50 relative overflow-hidden">
      {/* Background water animation */}
      <WaterAnimation />
      
      {/* Header */}
      <header className="relative z-10 bg-card/80 backdrop-blur-md border-b border-border shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-lg">
                <Droplet className="w-7 h-7 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Aquapure US
                </h1>
                <p className="text-sm text-muted-foreground">Environmental Impact Tracker</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden md:flex items-center gap-2 px-4 py-2 bg-accent/10 rounded-full border border-accent/30">
                <div className="w-2 h-2 bg-accent rounded-full animate-pulse"></div>
                <span className="text-sm font-medium text-accent-foreground">Live Updates</span>
              </div>
              <Button
                onClick={() => setShareOpen(true)}
                className="gap-2 bg-accent hover:bg-accent/90 text-accent-foreground shadow-md"
                data-testid="share-impact-open-btn"
              >
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">Share Our Impact</span>
                <span className="sm:hidden">Share</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 py-12">
        {/* Hero Counter */}
        <CounterDisplay 
          value={stats.totalBottlesSaved} 
          loading={loading}
        />

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <StatsCard
            icon={<Droplet className="w-6 h-6" />}
            title="Total Refills"
            value={stats.totalRefills.toLocaleString()}
            subtitle="5-gallon jugs refilled"
            color="primary"
          />
          <StatsCard
            icon={<Users className="w-6 h-6" />}
            title="Active Customers"
            value={stats.totalCustomers.toLocaleString()}
            subtitle="Making a difference"
            color="secondary"
          />
          <StatsCard
            icon={<TrendingUp className="w-6 h-6" />}
            title="This Month"
            value={stats.thisMonth.toLocaleString()}
            subtitle="Bottles saved"
            color="accent"
          />
        </div>

        {/* Impact Message */}
        <Card className="mt-12 p-8 bg-gradient-to-br from-primary/5 to-accent/5 border-primary/20">
          <div className="flex items-start gap-6">
            <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
              <Award className="w-8 h-8 text-accent" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-foreground mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Together, We're Making a Difference
              </h3>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Every refill prevents approximately <span className="font-semibold text-accent">37 single-use plastic bottles</span> from
                polluting our oceans and landfills. By choosing Aquapure US, you're not just getting premium water—you're
                protecting our planet for future generations.
              </p>
              <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
                <div className="w-2 h-2 rounded-full bg-accent"></div>
                <span>Based on standard 16.9 oz single-use bottles</span>
              </div>
            </div>
          </div>
        </Card>
      </main>

      {/* Footer */}
      <footer className="relative z-10 mt-12 border-t border-border bg-card/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Aquapure US · aquapureus.com · @aquapureus
            </p>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Droplet className="w-4 h-4 text-accent" />
              <span>Data updates every 30 seconds</span>
            </div>
          </div>
        </div>
      </footer>

      <ShareImpactCard
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        bottlesSaved={stats.totalBottlesSaved}
        totalRefills={stats.totalRefills}
      />
    </div>
  );
}
